import { useEffect } from "react";

/**
 * Tells the host page how tall the widget needs to be.
 *
 * An iframe has no intrinsic height, so without this the widget is either
 * permanently 620px with its own inner scrollbar, or clipped. Posting the
 * measured height lets the loader grow the frame so the page scrolls as one
 * document, which is what makes an embed feel like part of the site rather
 * than a box bolted onto it.
 *
 * A no-op when not framed, so the standalone app pays nothing for it.
 */
export function useEmbedHeight(): void {
  useEffect(() => {
    if (window.parent === window) return;

    let last = 0;

    const report = () => {
      const height = Math.ceil(document.documentElement.scrollHeight);

      // Sub-pixel reflows would otherwise post continuously, and every post
      // resizes the frame, which reflows again.
      if (Math.abs(height - last) < 8) return;
      last = height;

      // The loader verifies the origin and the source frame, so "*" here only
      // means we are not asserting who the host is - which we cannot know,
      // since any partner site may embed this.
      window.parent.postMessage({ type: "reassuremed:resize", height }, "*");
    };

    const observer = new ResizeObserver(report);
    observer.observe(document.documentElement);

    report();

    return () => observer.disconnect();
  }, []);
}

/** True when running inside a partner site's frame. */
export function isEmbedded(): boolean {
  return window.parent !== window;
}
